import { BackofficeCustomerCesuContractsIndexResponse } from '../json-schemas/serializers/backoffice-customer-cesu-contracts-index-response'
import { BackofficeCesuContractsCountPerScopeResponse } from '../json-schemas/serializers/backoffice-cesu-contracts-count-per-scope-response'
import { CesuContractBase } from '../json-schemas/base/cesu_contract'
import { CesuPayslipBase } from '../json-schemas/base/cesu_payslip'
import { CustomerBase } from '../json-schemas/base/customer'
import { EmployeeBase } from '../json-schemas/base/employee'
import { httpDelete, httpGet, httpGetRoute, httpParamsRemoveDiacriticsToSortableNames, httpPatch, httpPost, httpPostMultipart } from '../utils/http.service'
import { I18nService } from '../utils/i18n.service'
import CesuContractJobQualificationSchema from '../../../../../../json_schemas/cesu_contract/job-qualification.json'
import i18nScopedData from './cesu-contracts.service.yml'
import { toUpperCaseFirst } from '../utils/lodash.service'
import { formatTime, parseTimeRange, utilRange } from '../utils/utils.service'

const i18nScoped = new I18nService(i18nScopedData)
export const cesuContractJobQualifications = CesuContractJobQualificationSchema.enum as CesuContractBase['job_qualification'][]
export const cesuContractContractTypes: CesuContractBase['contract_type'][] = ['DSN_01', 'DSN_02']

export const cesuContractChargeExemptions: CesuContractBase['charge_exemption'][] = ['02', '01', '03', '05', '06', '70', '71', '80']
export const cesuContractManagementFeeUnitsOfWork: CesuContractBase['management_fee_unit_of_work'][] = ['hour', 'event']
export const cesuContractProcessingModes: CesuContractBase['processing_mode'][] = ['cesu_plus', 'cesu_socle_fees_and_salary', 'cesu_socle_fees_only', 'invoicing_fees_and_provisions', 'invoicing_fees_only']
export const cesuContractCesuApiProcessingModes = ['cesu_plus', 'cesu_socle_fees_and_salary', 'cesu_socle_fees_only'] as const satisfies CesuContractBase['processing_mode'][]
export const cesuContractInvoicingProcessingModes = ['invoicing_fees_and_provisions', 'invoicing_fees_only'] as const satisfies CesuContractBase['processing_mode'][]
export type CesuContractScope = 'draft' | 'awaiting_customer_signature' | 'awaiting_employee_signature' | 'validated_with_signature' | 'validated_skip_signing' | 'cesu_certificate_missing' | 'certificate_to_sign' | 'certificate_validated_in_progress' | 'certificate_validated_error' | 'cesu_socle_activated' | 'cesu_plus_activated' | 'invoicing_only_activated' | 'all'

export const cesuContractOnDutyPresenceTierCoefficients = {
  on_duty_presence_day: 2 / 3,
  on_duty_presence_night_full: 1,
  on_duty_presence_night_third: 1 / 3,
  on_duty_presence_night_quarter: 0.25
} as const

export interface RansackQuery {
  'q[customer_id_eq]'?: CustomerBase['id']
  'q[employee_id_eq]'?: EmployeeBase['id']
  'q[id_not_in][]'?: number[]
  'q[customer_sortable_name_or_phone_number_or_email_or_customer_code_cont]'?: string
  'q[employee_sortable_name_or_phone_number_or_email_or_formatted_counter_cont]'?: string
  from_date?: string
  to_date?: string
  scope?: CesuContractScope
}

export function cesuContractsList (query: RansackQuery): Promise<BackofficeCustomerCesuContractsIndexResponse> {
  return httpGet(`/${httpGetRoute()}/cesu_contracts`, httpParamsRemoveDiacriticsToSortableNames(query))
}

export function cesuContractCountPerScope (query: RansackQuery): Promise<BackofficeCesuContractsCountPerScopeResponse> {
  return httpGet(`/${httpGetRoute()}/cesu_contracts/count_per_scope`, query)
}

export function cesuContractI18nScope (scope: CesuContractScope): string {
  return i18nScoped.t(`scope.${scope}`)
}

export function cesuContractsCreate (body: Partial<CesuContractBase>): Promise<void> {
  return httpPost(`/${httpGetRoute()}/cesu_contracts`, { cesu_contract: body })
}

export function cesuContractsUpdate (body: any): Promise<void> {
  return httpPatch(`/${httpGetRoute()}/cesu_contracts/${body.id}`, { cesu_contract: body })
}

export function cesuContractEstimateContributionPercentages (params: Pick<CesuContractBase, 'estimated_monthly_hours' | 'gross_employee_hourly_salary_cents' | 'charge_exemption'>): Promise<{ employer_charge_percentage: number; employee_charge_percentage: number }> {
  return httpGet(`/${httpGetRoute()}/cesu_contracts/estimate_contribution_percentages`, params)
}

export function cesuContractUploadContract (id: CesuContractBase['id'], file: Blob): Promise<void> {
  const formData = new FormData()
  formData.append('file', file)
  return httpPostMultipart(`/${httpGetRoute()}/cesu_contracts/${id}/upload_contract`, formData) as Promise<void>
}

export function cesuContractSendContract (id: CesuContractBase['id'], alsoSendCertificate: boolean): Promise<void> {
  return httpPost(`/${httpGetRoute()}/cesu_contracts/${id}/send_contract`, { also_send_certificate: alsoSendCertificate }) as Promise<void>
}

export function cesuContractGenerateAheadOfTimePayslip (id: CesuContractBase['id']): Promise<{ id: CesuPayslipBase['id'] | null }> {
  return httpPost(`/${httpGetRoute()}/cesu_contracts/${id}/generate_ahead_of_time_payslip`, {})
}

export function cesuContractDestroy (id: CesuContractBase['id']): Promise<void> {
  return httpDelete(`/${httpGetRoute()}/cesu_contracts/${id}`)
}

export function cesuContractI18nChargeExemption (code: CesuContractBase['charge_exemption'], { capitalize = true } = {}): string {
  const translated = i18nScoped.t(`charge_exemption.${code}`)
  return capitalize ? toUpperCaseFirst(translated) : translated
}

export function cesuContractI18nContractType (value: CesuContractBase['contract_type']): string {
  return i18nScoped.t(`contract_type.${value}`)
}

export function cesuContractI18nJobQualification (value: CesuContractBase['job_qualification']): string {
  return i18nScoped.t(`job_qualification.${value}`)
}

export function cesuContractI18nManagementFeeUnitOfWork (value: CesuContractBase['management_fee_unit_of_work']): string {
  return i18nScoped.t(`management_fee_unit_of_work.${value}`)
}

export function cesuContractProcessingModeLabel (value: CesuContractBase['processing_mode']): string {
  return i18nScoped.t(`processing_mode.${value}.label`)
}

export function cesuContractProcessingModeDescription (value: CesuContractBase['processing_mode']): string {
  return i18nScoped.t(`processing_mode.${value}.description`)
}

export function cesuContractI18nOverworkPaid (paid: boolean): string {
  return i18nScoped.t(`overwork_paid.${paid}`)
}

export function cesuContractI18nRestWeekDayWorkedResolution (paid: boolean, short = false): string {
  return i18nScoped.t(`rest_week_day_worked_paid.${paid}${short ? '_short' : ''}`)
}

export function cesuContractIsCesuApiProcessingMode (mode: CesuContractBase['processing_mode']): boolean {
  return (cesuContractCesuApiProcessingModes as readonly string[]).includes(mode)
}

// The night on-duty presence window can be stretched by up to 1h30 on each side of the
// 20:00-06:30 default (18:30 at the earliest / 08:00 at the latest), capped at 12h total.
export function cesuContractOnDutyPresenceNightTimeOptions (start: string, end: string): { startOptions: string[], endOptions: string[] } {
  const nightDurationHours = (from: string, until: string) => {
    const [parsedFrom, parsedUntil] = parseTimeRange(from, until)
    return parsedUntil - parsedFrom
  }

  return {
    startOptions: utilRange(18.5, 24, 0.25).map(formatTime).filter(value => nightDurationHours(value, end) <= 12),
    endOptions: utilRange(0, 8.01, 0.25).map(formatTime).filter(value => nightDurationHours(start, value) <= 12)
  }
}

// Mirrors the tier thresholds of CesuPriceable#cesu_on_duty_presence_specific_increases (app/models/concerns/cesu_priceable.rb).
export function cesuContractOnDutyPresenceNightTierForInterruptCount (count: number) {
  if (count >= 4) return 'on_duty_presence_night_full' as const
  if (count >= 2) return 'on_duty_presence_night_third' as const
  return 'on_duty_presence_night_quarter' as const
}

type CesuContractStateObject = { text: string, colorClassName: string, iconClassName: string }

export function cesuContractStateAsObject (state: CesuContractBase['state'], cesuContract?: Pick<CesuContractBase, 'skip_signing'>, verbose = false): CesuContractStateObject {
  const prefix = verbose ? 'state_verbose' : 'state'
  const text = cesuContract?.skip_signing && state === 'validated'
    ? i18nScoped.t(`${prefix}.validated_no_signing`)
    : i18nScoped.t(`${prefix}.${state}`)
  switch (state) {
    case 'draft': return { text, colorClassName: 'u-txtClr2', iconClassName: 'icon-edit' }
    case 'created': return { text, colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
    case 'partially_signed': return { text, colorClassName: 'u-txtClr3', iconClassName: 'icon-clock' }
    case 'validated': return { text, colorClassName: 'u-txtClrSuccess', iconClassName: 'icon-check' }
  }
}
